// Global Instructions Rule Applied!
//
// LP Allocation grid data (LP agents UAT follow-ups PR E): the canonical
// grid pattern (Asset Transactions is the sibling) — the Redux slice holds
// rows + active filters / sorting + paging, user preferences hydrate and
// persist filters, sorting and column order through
// /global-configs/user-preferences, filter options load once, and load-more
// appends the next page for TableView's infinite scroll.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  setLpAllocation,
  appendLpAllocation,
  setLpAllocationActiveFilters,
  setLpAllocationActiveSorting,
  updateLpAllocationRow
} from '../../../state_manager/state_update_actions/lp_allocation/index.ts'
import { fetchLpAllocation, fetchLpAllocationFilterOptions } from '../services/relationshipsService.ts'
import { LpAllocationRow } from '../../../state_manager/data_models/relationships_model.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  areSortConfigsEqual,
  FilterValue,
  SortConfig,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow
} from '../../global_configs/tableViewPreferences.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import { MENU_ITEM_KEY_LP_ALLOCATION } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useLpAllocationReportId } from '../../../state_manager/hooks/useLpAllocationReportId.ts'

const DEFAULT_LP_ALLOCATION_FILTERS: FilterValue[] = []
const PAGE_SIZE = 100

export const useLpAllocation = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { menuItemId, preferenceRow, persistPreference, deletePreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_LP_ALLOCATION
  )
  const reportId = useLpAllocationReportId()
  const defaultTableSorting = useReportingDefaultSorting(reportId)

  const [dataLoading, setDataLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [prefsReady, setPrefsReady] = useState(false)
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const skipPersistRef = useRef(true)

  const data = useSelector((state: any) => (state.lpAllocation?.data ?? []) as LpAllocationRow[])
  const totalCount = useSelector((state: any) => (state.lpAllocation?.totalCount ?? 0) as number)
  const activeFilters = useSelector((state: any) => (state.lpAllocation?.activeFilters ?? []) as FilterValue[])
  const activeSorting = useSelector((state: any) => (state.lpAllocation?.activeSorting ?? []) as SortConfig[])
  const hasMore = useSelector((state: any) => (state.lpAllocation?.hasMore ?? false) as boolean)
  const currentPage = useSelector((state: any) => (state.lpAllocation?.currentPage ?? 1) as number)
  const menuItems = useSelector((state: any) => state.globalConfigs?.menu_items ?? [])

  useEffect(() => {
    if (menuItems.length === 0) return
    if (!menuItemId) {
      dispatch(setLpAllocationActiveFilters({ filters: DEFAULT_LP_ALLOCATION_FILTERS }))
      dispatch(setLpAllocationActiveSorting({ sorting: defaultTableSorting }))
      setPrefsReady(true)
      return
    }
    const filters = resolveTableFiltersFromPreferenceRow(preferenceRow, DEFAULT_LP_ALLOCATION_FILTERS)
    const sorting = resolveTableSortingFromPreferenceRow(preferenceRow, defaultTableSorting)
    if (!areFilterValuesEqual(filters, activeFilters)) dispatch(setLpAllocationActiveFilters({ filters }))
    if (!areSortConfigsEqual(sorting, activeSorting)) dispatch(setLpAllocationActiveSorting({ sorting }))
    setPrefsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemId, menuItems.length, preferenceRow, defaultTableSorting, dispatch])

  const handleFetch = useCallback(
    async (sorting: SortConfig[] = [], filters?: FilterValue[]) => {
      try {
        setDataLoading(true)
        const token = getAccessToken(store.getState().auth)
        const res = await fetchLpAllocation(
          { sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })), filters: filters ?? [], page: 1, page_size: PAGE_SIZE },
          token
        )
        dispatch(setLpAllocation({ response: { data: res.data, hasMore: res.has_more, totalCount: res.count } }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching LP allocation:', e)
      } finally {
        setDataLoading(false)
      }
    },
    [dispatch]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      dispatch(setLpAllocationActiveFilters({ filters }))
    },
    [dispatch, activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      dispatch(setLpAllocationActiveSorting({ sorting }))
    },
    [dispatch, activeSorting]
  )

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      dispatch(setLpAllocationActiveFilters({ filters: DEFAULT_LP_ALLOCATION_FILTERS }))
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) return
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      skipPersistRef.current = true
      dispatch(setLpAllocationActiveFilters({ filters: DEFAULT_LP_ALLOCATION_FILTERS }))
      dispatch(setLpAllocationActiveSorting({ sorting: defaultTableSorting }))
    })()
  }, [dispatch, menuItemId, deletePreference, defaultTableSorting])

  const persistMergedPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const merged: Record<string, unknown> = {
        ...(row?.data ?? {}),
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        else {
          const user = store.getState().auth.user
          if (user) await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => persistMergedPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames }),
    [persistMergedPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const base: Record<string, unknown> = { ...(row?.data ?? {}) }
    delete base[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = { ...base, [PREF_TABLE_FILTERS]: activeFilters, [PREF_TABLE_SORTING]: activeSorting }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      if (hadExistingRow) dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      else {
        const user = store.getState().auth.user
        if (user) await fetchGlobalConfigsThunk(user)(dispatch)
      }
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const handleLoadMore = useCallback(
    async (page: number, pageSize: number) => {
      if (loadingMore || !hasMore) return
      try {
        setLoadingMore(true)
        const token = getAccessToken(store.getState().auth)
        const res = await fetchLpAllocation(
          {
            sorting: activeSorting.map((s) => ({ key: s.key, direction: s.direction })),
            filters: activeFilters,
            page,
            page_size: pageSize || PAGE_SIZE
          },
          token
        )
        dispatch(appendLpAllocation({ response: { data: res.data, hasMore: res.has_more, totalCount: res.count } }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Error loading more LPs:', e)
      } finally {
        setLoadingMore(false)
      }
    },
    [dispatch, activeFilters, activeSorting, hasMore, loadingMore]
  )

  useEffect(() => {
    if (!prefsReady) return
    const token = getAccessToken(store.getState().auth)
    if (!token) return
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      try {
        const res = await fetchLpAllocationFilterOptions(token)
        if (!cancelled) setFilterOptions(res as unknown as Record<string, Array<{ value: string; label: string }>>)
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('LP allocation filter options failed:', e)
      } finally {
        if (!cancelled) setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [prefsReady])

  useEffect(() => {
    if (!prefsReady) return
    const token = getAccessToken(store.getState().auth)
    if (token) handleFetch(activeSorting, activeFilters)
  }, [prefsReady, activeFilters, activeSorting, handleFetch])

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') console.error('Failed to persist LP allocation preferences:', err)
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedPreference])

  const patchRow = useCallback(
    (accountId: string, updates: Partial<LpAllocationRow>) => dispatch(updateLpAllocationRow({ accountId, updates })),
    [dispatch]
  )

  return {
    data,
    totalCount,
    dataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetch,
    refetch: () => handleFetch(activeSorting, activeFilters),
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    patchRow,
    defaultTableFilters: DEFAULT_LP_ALLOCATION_FILTERS,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  }
}
